import LZString from 'lz-string';

// ── Workflow canvas share (hash #w=) ─────────────────────────────────────────

/**
 * Compresses a canvas state object into a URL-safe hash fragment.
 * The returned URL uses #w=<compressed> so it is distinct from the pipeline
 * share format (#p=).  No backend or database is required.
 *
 * @param {object} canvasState  Output of serializeCanvas()
 * @returns {string}  Full URL with #w=<compressed>, or '' on error
 */
export function workflowToShareUrl(canvasState) {
  try {
    if (!canvasState || typeof canvasState !== 'object') return '';
    const json = JSON.stringify(canvasState);
    const compressed = LZString.compressToEncodedURIComponent(json);
    const base = window.location.origin + window.location.pathname + (window.location.search || '');
    return `${base}#w=${compressed}`;
  } catch {
    return '';
  }
}

/**
 * Reads a workflow canvas state from location.hash (#w=...).
 * Returns null on parse error, missing hash, or invalid shape.
 *
 * @returns {{ version: number, nodes: object[], edges: object[] } | null}
 */
export function loadWorkflowFromUrl() {
  try {
    const hash = window.location.hash;
    if (!hash || typeof hash !== 'string') return null;
    const match = hash.match(/^#w=(.+)$/);
    if (!match || !match[1]) return null;
    const decoded = LZString.decompressFromEncodedURIComponent(match[1]);
    if (!decoded) return null;
    const parsed = JSON.parse(decoded);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !Array.isArray(parsed.nodes) ||
      parsed.nodes.length === 0
    ) {
      console.warn('[sharePipeline] Invalid workflow canvas in URL hash');
      return null;
    }
    return parsed;
  } catch (e) {
    console.warn('[sharePipeline] Failed to load workflow from URL:', e);
    return null;
  }
}

// ── Pipeline share (hash #p=) ─────────────────────────────────────────────────

/**
 * Validates that a pipeline is an array of agents with required fields.
 * @param {unknown} pipeline
 * @returns {boolean}
 */
function validatePipeline(pipeline) {
  if (!Array.isArray(pipeline) || pipeline.length === 0) return false;
  const valid = pipeline.every(
    (p) =>
      p &&
      typeof p === 'object' &&
      typeof p.id === 'string' &&
      typeof p.name === 'string' &&
      typeof p.systemPrompt === 'string' &&
      typeof p.model === 'string'
  );
  return valid;
}

/**
 * Serializes the pipeline and returns a full URL with compressed payload in the hash.
 * Only includes pipeline data (no API keys, files, or secrets).
 * @param {Array<{id: string, name: string, systemPrompt: string, model: string}>} pipeline
 * @returns {string} Full URL with #p=...
 */
export function pipelineToShareUrl(pipeline) {
  try {
    if (!Array.isArray(pipeline) || pipeline.length === 0) return '';
    const payload = pipeline.map((p) => ({
      id: p.id,
      name: p.name,
      systemPrompt: p.systemPrompt,
      model: p.model,
    }));
    const json = JSON.stringify(payload);
    const compressed = LZString.compressToEncodedURIComponent(json);
    const base = window.location.origin + window.location.pathname + (window.location.search || '');
    return `${base}#p=${compressed}`;
  } catch {
    return '';
  }
}

/**
 * Reads pipeline from location.hash (#p=...).
 * Returns null on parse error or invalid pipeline.
 * @returns {Array<{id: string, name: string, systemPrompt: string, model: string}> | null}
 */
export function loadPipelineFromUrl() {
  try {
    const hash = window.location.hash;
    if (!hash || typeof hash !== 'string') return null;
    const match = hash.match(/^#p=(.+)$/);
    if (!match || !match[1]) return null;
    const compressed = match[1];
    const decoded = LZString.decompressFromEncodedURIComponent(compressed);
    if (!decoded) return null;
    const parsed = JSON.parse(decoded);
    if (!validatePipeline(parsed)) {
      console.warn('[sharePipeline] Invalid pipeline in URL hash: missing or invalid agent fields');
      return null;
    }
    return parsed;
  } catch (e) {
    console.warn('[sharePipeline] Failed to load pipeline from URL:', e);
    return null;
  }
}
